import { useState, useEffect, useCallback } from 'react';
import { useGetOrders } from '../../../../api/services/lupp/order/order';

type UseOrdersTableParams = {
  enabled?: boolean;
};

export const useOrdersTable = ({ enabled = true }: UseOrdersTableParams = {}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Sort state
  const [sortField, setSortField] = useState<string>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes
      setCurrentPage(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch orders data
  const {
    data: ordersResponse,
    isLoading,
    isFetching,
    error,
  } = useGetOrders({
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch,
    sortField,
    sortOrder,
    enabled,
  });

  // Handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortOrder(direction);
    setCurrentPage(0); // Reset to first page when sort changes
  }, []);

  return {
    // Data
    data: ordersResponse?.items || [],
    totalCount: ordersResponse?.totalCount || 0,

    // Loading states
    isLoading,
    isFetching,
    error,

    // Pagination state
    currentPage,
    pageSize,

    // Search state
    searchQuery,
    debouncedSearch,

    // Sort state
    sortField,
    sortOrder,

    // Handlers
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSearchChange: handleSearchChange,
    onSort: handleSort,
  };
};
